import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Check, Sparkles, Loader2, Key, Shield, HeartCrack, MessageSquare, Wrench, Heart, MapPin, Flame } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const FEATURES = [
  { icon: MessageSquare, label: "Unlimited AI Coach chat + voice input" },
  { icon: Wrench, label: "All interactive tools (text reply, opener, situation analyzer, reconnect)" },
  { icon: Heart, label: "First-Date Playbook with progress tracking" },
  { icon: MapPin, label: "Date Night Planner for any city" },
  { icon: HeartCrack, label: "30-Day No-Contact Recovery Program" },
  { icon: Flame, label: "Daily lessons, streak tracking & weekly check-ins" },
];

const Upgrade = () => {
  const { access, refreshAccess } = useAuth();
  const [promo, setPromo] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const navigate = useNavigate();

  const redeem = async () => {
    if (!promo.trim()) return;
    setRedeeming(true);
    try {
      const r = await api.post("/promo/redeem", { code: promo.trim() });
      toast.success("Code redeemed. Welcome in.");
      await refreshAccess();
      setPromo("");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    }
    setRedeeming(false);
  };

  const subscribe = async () => {
    setSubscribing(true);
    try {
      const r = await api.post("/subscription/create");
      window.location.href = r.data.approve_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout unavailable. Try again shortly.");
      setSubscribing(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription? You'll keep access until the end of the current billing period.")) return;
    setCancelling(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Subscription cancelled. You'll keep access until period end.");
      await refreshAccess();
    } catch (e) {
      toast.error("Could not cancel. Contact support.");
    }
    setCancelling(false);
  };

  const isActive = access?.status === "active";
  const isGrand = access?.status === "grandfathered";
  const isPromo = access?.status === "promo";

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <div className="overline mb-2">MEMBERSHIP</div>
      <h1 className="font-serif text-4xl sm:text-5xl mb-4 leading-tight" data-testid="upgrade-title">
        {isActive || isGrand || isPromo ? "You're " : "Unlock "}
        <span className="italic text-amber-500">Suitor Premium</span>
      </h1>
      <p className="text-neutral-400 max-w-xl mb-10">
        {isGrand ? "You're grandfathered in — premium is free for you, forever. Thanks for being here early." :
         isActive ? "Your subscription is active. Thank you for backing Suitor." :
         isPromo ? "Access via promo code. You're all set." :
         "7-day free trial, then $19/month. Cancel anytime from this page."}
      </p>

      <div className="grid md:grid-cols-5 gap-8">
        {/* Main pricing card */}
        <div className="md:col-span-3 card-surface p-8 relative overflow-hidden">
          <div className="overline text-amber-500 mb-2">SUITOR PREMIUM</div>
          <div className="flex items-end gap-2 mb-4">
            <span className="font-serif text-6xl">$19</span>
            <span className="text-neutral-400 pb-2">/ month</span>
          </div>
          <div className="text-sm text-neutral-400 mb-6">7-day free trial · Cancel anytime · Billed via PayPal</div>

          <ul className="space-y-3 mb-8">
            {FEATURES.map((f, i) => (
              <li key={i} className="flex items-start gap-3 text-neutral-200">
                <div className="w-7 h-7 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center shrink-0">
                  <f.icon className="w-3.5 h-3.5 text-amber-400" />
                </div>
                <span>{f.label}</span>
              </li>
            ))}
          </ul>

          {isActive ? (
            <button onClick={cancel} disabled={cancelling} className="btn-ghost inline-flex items-center gap-2 disabled:opacity-60" data-testid="cancel-btn">
              {cancelling && <Loader2 className="w-4 h-4 animate-spin"/>} Cancel subscription
            </button>
          ) : isGrand || isPromo ? (
            <div className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-sm">
              <Check className="w-4 h-4" /> Premium active — no payment needed
            </div>
          ) : (
            <button onClick={subscribe} disabled={subscribing} className="btn-amber inline-flex items-center gap-2 disabled:opacity-60" data-testid="subscribe-btn">
              {subscribing && <Loader2 className="w-4 h-4 animate-spin"/>}
              Start 7-day free trial via PayPal
            </button>
          )}
          <p className="text-xs text-neutral-500 mt-4">
            Payments processed securely by PayPal. No charges during the trial.
          </p>
        </div>

        {/* Promo code card */}
        <div className="md:col-span-2 card-surface p-8">
          <div className="flex items-center gap-2 mb-3">
            <Key className="w-5 h-5 text-amber-500" />
            <div className="overline">PROMO CODE</div>
          </div>
          <h3 className="font-serif text-2xl mb-2">Got a code?</h3>
          <p className="text-neutral-400 text-sm mb-5">
            Enter your code to unlock premium instantly — no payment required.
          </p>
          <input
            type="text"
            value={promo}
            onChange={(e) => setPromo(e.target.value.toUpperCase())}
            placeholder="COACHCLIENT"
            className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white tracking-wider focus:outline-none focus:border-amber-500/50 mb-4"
            data-testid="promo-input"
          />
          <button
            onClick={redeem}
            disabled={redeeming || !promo.trim() || isGrand || isActive}
            className="btn-amber w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="redeem-btn"
          >
            {redeeming && <Loader2 className="w-4 h-4 animate-spin"/>} Redeem code
          </button>
        </div>
      </div>

      <div className="mt-10 flex items-center justify-center gap-2 text-xs text-neutral-500">
        <Shield className="w-3 h-3" /> Secure checkout · No long-term contract · 1-click cancel
      </div>
    </div>
  );
};

export default Upgrade;
